import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { DeviceBookingRequest } from '@/types/scheduling-api';
import { classifyDeviceBookingFailure } from '@/scheduling/lib/device-booking-errors';
import { RESOURCE_AVAILABILITY_KEY } from './use-device-availability';
import { APPOINTMENT_LIST_QUERIES } from './appointment-list-keys';

interface BookResponse {
  id: string;
}

/**
 * The route every device booking goes to (PROD-2383).
 *
 * `POST /api/Appointment/BookResource` is the DEVICE's own booking route
 * (Scheduling PROD-2385, #1984): the same resolver and booking engine that
 * `POST /api/Appointment/Book` runs for a `deviceId`, behind a request shape
 * with no practitioner fields. Andrew's standing ruling (2026-09-13) is that
 * the device APIs mirror the provider ones and never overload the provider
 * route, and a recurring device COURSE exists only here — `Book` refuses a
 * device recurrence (`DeviceRecurringOnProviderRoute`). One route for single
 * sessions and courses alike, rather than two for the same button.
 */
export const BOOK_DEVICE_URL = '/api/Appointment/BookResource';

/**
 * The device route's body. The builder's payload is the `Book` shape, which
 * `BookResource` accepts field for field except ONE: a caller-chosen billing
 * profile travels top-level (`CreateResourceAppointmentRequest.BillingProfileId`)
 * rather than nested on `appointmentProfile`. Everything the device route has
 * no field for (`specialty`, `invoice`, `appointmentProfile`, …) is ignored by
 * the server, so it is left as the builder made it.
 */
export function toResourceBookingBody(request: DeviceBookingRequest) {
  return {
    ...request,
    billingProfileId: request.appointmentProfile?.billingProfileId ?? null,
  };
}

/**
 * Book a device — PROD-2007, Scheduling PR #1964 (S12); PROD-2383 moved it to
 * the device route and taught it recurring courses.
 *
 *   POST /api/Appointment/BookResource   body: DeviceBookingRequest (+ recurring)
 *
 * Refusals arrive as 400 `ValidationProblemDetails`; the panel maps them with
 * `classifyDeviceBookingFailure` and owns the messaging, so the global
 * "Something went wrong" toast is opted out. On success the calendar list and
 * every availability query are invalidated — the device's window is gone.
 * A course that stopped part-way is an ERROR that still put sessions on the
 * calendar, so it invalidates the same queries: the booked sessions must show.
 */
export function useBookDevice() {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries(APPOINTMENT_LIST_QUERIES);
    void queryClient.invalidateQueries({ queryKey: [RESOURCE_AVAILABILITY_KEY] });
  };
  return useMutation({
    mutationKey: ['book-device'],
    mutationFn: (request: DeviceBookingRequest) =>
      post<BookResponse>(BOOK_DEVICE_URL, toResourceBookingBody(request)),
    meta: { skipGlobalError: true },
    onSuccess: invalidate,
    onError: (err) => {
      if (classifyDeviceBookingFailure(err).code === 'RecurringPartiallyBooked') invalidate();
    },
  });
}
